import { CartAPI } from '../../api/cart-api'
import { fetchConfig } from '../../utils/fetch-config'
import { NotificationComponent } from '../../components/notification/notification-component'
import { SidebarComponent } from '../../components/sidebar/sidebar'
import { getTemplateFirstChild, parseHTML } from '../../utils/dom'
import { debounce } from '../../utils/debounce'
import { CartItem } from '../cart/cart-item'
import { $el, $list, delay, transitionToPromise } from '../../utils/utils'
import { whenTransitionEnds } from 'src/scripts/utils/transition-util'

const VIEWPORT_SELECTOR = '[data-cart-drawer-viewport]'
const RESULTS_SELECTOR = '[data-cart-drawer-results]'
const NOTIFICATION_SELECTOR = '#CartDrawerNotification'
const CART_ITEM_SELECTOR = 'cart-item'

export class CartDrawer extends SidebarComponent {
  cartAPI: CartAPI = new CartAPI()
  resizeObserver: ResizeObserver
  lastDrawerBody: any
  error: string

  mountComponent(): void {
    super.mountComponent()

    const viewport = $el(VIEWPORT_SELECTOR, this.element)
    const results = $el(RESULTS_SELECTOR, this.element)

    if (results) {
      this.resizeObserver = new ResizeObserver(this.handleResultsResize)
      this.resizeObserver.observe(results)
    }

    this.addListener(this, 'change', this.handleCartChange)
    this.addListener(this, 'change', this.handleItemChange)
    this.addListener(viewport, 'scroll', this.handleViewportScroll)
  }

  unmountComponent(): void {
    super.unmountComponent()
    this.resizeObserver.disconnect()
  }

  handleItemChange = ({ target }): void => {
    const item: CartItem = $el(`#CartItem-${+target.dataset.index}`, this)
    item?.setLoading(true)
  }

  handleCartChange = debounce(({ target }): void => {
    this.updateItem(+target.dataset.index, +target.value)
  }, 500)

  handleViewportScroll = debounce((): void => {
    this.updateViewport()
  }, 25)

  handleResultsResize = debounce((): void => {
    this.updateViewport()
  }, 25)

  updateViewport(): void {
    const results = $el(RESULTS_SELECTOR, this.element)
    const viewport = $el(VIEWPORT_SELECTOR, this.element)

    if (!results || !viewport) {
      return
    }

    const isOverflow = results.offsetHeight > viewport.offsetHeight
    const isScrolled = viewport.scrollTop > 0

    this.classList.toggle('header-shadow-visible', isOverflow && isScrolled)
    this.classList.toggle('footer-shadow-visible', isOverflow)
  }

  update() {
    this.cartAPI
      .getDrawer()
      .then(responseText => {
        const html = parseHTML(responseText)
        this.lastDrawerBody = this.getTemplateContent(html)
      })
      .catch(() => {
        this.showError('')
      })
      .finally(() => {
        this.mount()
        this.emit('update-nodes', {
          node: this.lastDrawerBody,
        })
        this.updateEmptyStatus()
      })
  }

  updateItem(itemIndex: number, quantity: number): Promise<void> {
    const item: CartItem = $el(`#CartItem-${itemIndex}`, this)

    if (!item) {
      return
    }

    const currentQuantity = +(item.dataset.quantity || 0)

    if (currentQuantity === quantity) {
      item.setLoading(false)
      return
    }

    item.setDisabled(true)

    if (quantity === 0) {
      item.removeItem()
      whenTransitionEnds(item, () => {
        const items = $list(CART_ITEM_SELECTOR, this.element)
        this.setPreEmpty(items.length === 1)
      })
    }

    this.emit('start-update', {})

    this.cartAPI
      .change({
        ...fetchConfig(),
        body: JSON.stringify({
          line: itemIndex + 1,
          quantity,
          sections: this.dataset.sectionId,
          sections_url: window.location.pathname,
        }),
      })
      .then(state => {
        const data = JSON.parse(state)

        if (data.errors) {
          throw new Error(data.errors)
        }

        this.lastDrawerBody = this.getTemplateContent(
          parseHTML(data.sections[this.dataset.sectionId]),
        )
      })
      .catch(error => {
        this.error = error
      })
      .finally(() => {
        this.mount()
        this.emit('update-nodes', {
          node: this.lastDrawerBody,
        })
        this.updateEmptyStatus()
        this.initFocus()
        item.setLoading(false)
        item.setDisabled(false)

        if (this.error) {
          this.showError(this.error)
          this.error = null
        }
      })
  }

  showError(text: string): void {
    const notification: NotificationComponent = $el(NOTIFICATION_SELECTOR, this)

    const message = text ? text : window.auroraThemeLocales.cartStrings.error

    notification?.show(message, 'warning', 6000)
  }

  async purchaseHandler(html, parsedState): Promise<void> {
    this.setPreEmpty(false)
    const node = this.getTemplateContent(html)

    this.mount()

    this.emit('update-nodes', { node, parsedState })

    this.updateEmptyStatus()

    await this.open()

    this.emit('purchase', { parsedState })

    this.addListener(this, 'showAsNew', this.updateLastDrawerBody)
  }

  updateLastDrawerBody = () => {
    this.lastDrawerBody = this.element.cloneNode(true)
  }

  setPreEmpty(isEmpty: boolean) {
    this.classList.toggle('is-pre-empty', isEmpty)
  }

  getTemplateContent(html) {
    const newTemplate = $el(`[data-sidebar-template="${this.id}"]`, html)

    return getTemplateFirstChild(newTemplate)
  }

  updateEmptyStatus(): void {
    const items = $list(CART_ITEM_SELECTOR, this.element)

    if (this.hasAttribute('data-has-warning-placeholder')) {
      this.toggleAttribute('is-empty', items.length === 0)
    }

    if (items.length === 0) {
      this.emit('cartIsEmpty', {})
    }
  }

  updateAsideProductsByParsedState(parsedState): void {
    this.toggleAttribute('has-aside-products', parsedState.quantity > 0)
  }

  async open() {
    await super.open()

    this.lastDrawerBody = this.element.cloneNode(true)
  }
}
